import { em } from 'polished'
import PropTypes from 'prop-types'
import React, { Component } from 'react'

import ButtonRoundedBase from 'components/ButtonRoundedBase'

import {
  decreaseWidthToLeftAndHideText,
  decreaseWidthToCenterAndHideText,
  showLoader,
  showLoaderProcess,
  hideLoader,
  showSuccessIcon,
  showFailedIcon,
} from './animations'

import './ButtonAsyncAction.scss'

const ANIM_LEFT = 'left'
const ANIM_CENTER = 'center'

/**
 * Component of Button with rounded corners
 * @param {object} props — properties of Component
 * @return {Component} Component — ButtonAsyncAction
 */
export default class ButtonAsyncAction extends Component {
  static propTypes = {
    /** Align of button's animation */
    animAlign: PropTypes.oneOf([ANIM_LEFT, ANIM_CENTER]),

    /** Title of button */
    children: PropTypes.node,

    /** Callback when failed animation ended */
    failureCb: PropTypes.func,

    /** Flag to control disable state */
    isDisabled: PropTypes.bool,

    /** Flag for server action state */
    isLoading: PropTypes.bool,

    /** Flag to show success of async action */
    isSucceed: PropTypes.bool,

    /** Callback when successful animation ended */
    successCb: PropTypes.func,

    /** Click on the button callback */
    onClick: PropTypes.func,

    /** Button background class name */
    bgClassName: PropTypes.string,

    /** ClassName with color for blackout tag */
    blackoutClassName: PropTypes.string,

    /** Class names wrapper component */
    wrapperClassName: PropTypes.string,
  }

  static defaultProps = {
    isLoading: false,
    animAlign: ANIM_LEFT,
    isDisabled: false,
  }

  constructor(props) {
    super(props)

    this.loader = React.createRef()
    this.loadingTimeline = null
    this.alignedAnimation = this.getAlignedAnimation(props)
    this.state = {
      prevLoading: false,
      prevSucceed: null,

      /*
       * Different conditions:
       * 0 — awaiting of first click
       * 1 — decreasing animation
       * 2 — (content changed) loader appearance
       * 3 — loader
       * 4 - loader hiding
       * 5 — async action status
       */
      condition: 0,
    }
  }

  /**
   * getDerivedStateFromProps
   * @param {object} nextProps — next properties of Component
   * @param {object} prevState — previous state of Component
   * @return {object} nextState
   */
  static getDerivedStateFromProps(nextProps, prevState) {
    const { isLoading, isSucceed } = nextProps
    const { prevLoading, prevSucceed, condition } = prevState

    if (isLoading !== prevLoading || isSucceed !== prevSucceed) {
      return {
        // We might change condition (to loading condition) of the button remotely by pass in isLoading flag
        condition: isLoading && condition === 0 ? 1 : condition,
        prevLoading: isLoading,
        prevSucceed: isSucceed,
      }
    }

    return null
  }

  componentDidUpdate(prevProps, prevState) {
    const { animAlign } = this.props
    const { prevLoading, condition } = this.state

    // Async action ended or
    // Async action ended during loader appearance animation
    // (we already have prevLoading - false and is getting condition 3 right now)
    if (
      (!prevLoading && prevLoading !== prevState.prevLoading) ||
      (!prevLoading && condition === 3 && condition !== prevState.condition)
    ) {
      if (this.loadingTimeline) {
        // If response comes from server we set here a duration to end loading state
        this.loadingTimeline.duration(0.6).eventCallback('onComplete', () => {
          this.loadingCb()
        })
      }
    }

    if (animAlign !== prevProps.animAlign) {
      this.alignedAnimation = this.getAlignedAnimation(this.props)
    }
  }

  componentWillUnmount() {
    if (this.loadingTimeline) {
      this.loadingTimeline.kill()
    }
  }

  getAlignedAnimation = props => {
    const { animAlign } = props

    return {
      decrease:
        animAlign === ANIM_CENTER
          ? decreaseWidthToCenterAndHideText
          : decreaseWidthToLeftAndHideText,
    }
  }

  clickAction = () => {
    const { onClick } = this.props
    let asyncStart = true

    if (onClick && typeof onClick === 'function') {
      asyncStart = onClick()
    }

    if (asyncStart) {
      this.setState({
        condition: 1,
      })
    }
  }

  decreasingCb = () => {
    this.setState({
      condition: 2,
    })
  }

  appearanceCb = () => {
    this.loadingTimeline = showLoaderProcess(this.loader.current)

    this.setState({
      condition: 3,
    })
  }

  loadingCb = () => {
    this.setState({
      condition: 4,
    })
  }

  fadeoutCb = () => {
    this.setState({
      condition: 5,
    })
  }

  renderPreloader = () => {
    const { animAlign } = this.props
    const styles = {
      display: 'block',
    }
    if (animAlign === 'left') {
      styles.width = em(50)
      styles.height = em(50)
    }

    return (
      <svg
        style={styles}
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        x="0px"
        y="0px"
        width="100%"
        height="100%"
        viewBox="-60 -60 320 320"
      >
        <path
          fill="none"
          stroke="#FFFFFF"
          strokeWidth="8"
          strokeOpacity=".2"
          strokeMiterlimit="10"
          d="M100.416,2.001C154.35,2.225,198,46.015,198,100c0,54.124-43.876,98-98,98S2,154.124,2,100S45.876,2,100,2"
        />
        <path
          ref={this.loader}
          className="path"
          fill="none"
          stroke="#FFFFFF"
          strokeWidth="15"
          strokeMiterlimit="10"
          strokeDasharray="1000"
          strokeDashoffset="1000"
          d="M100.416,2.001C154.35,2.225,198,46.015,198,100c0,54.124-43.876,98-98,98S2,154.124,2,100S45.876,2,100,2"
        />
      </svg>
    )
  }

  render() {
    const {
      children,
      isSucceed,
      successCb,
      failureCb,
      isDisabled,
      bgClassName,
      blackoutClassName,
      wrapperClassName,
    } = this.props
    const { condition } = this.state
    const btnProps = {
      as: 'button',
      wrapperClassName,
      wrapperProps: {},
      notHoverable: true,
      isDisabled,
    }
    let content = children

    if (condition === 0) {
      btnProps.wrapperProps.onClick = this.clickAction
      btnProps.notHoverable = false
      if (bgClassName) {
        btnProps.bgClassName = 'bg--black'
      }
      if (blackoutClassName) {
        btnProps.blackoutClassName = 'bg--purple'
      }
    } else if (condition === 1) {
      btnProps.animTimeline = this.alignedAnimation.decrease
      btnProps.animTimelineCb = this.decreasingCb
    } else if (condition === 2) {
      content = this.renderPreloader()
      btnProps.animTimeline = showLoader
      btnProps.animTimelineCb = this.appearanceCb
    } else if (condition === 3) {
      content = this.renderPreloader()
    } else if (condition === 4) {
      content = this.renderPreloader()
      btnProps.animTimeline = hideLoader
      btnProps.animTimelineCb = this.fadeoutCb
    } else if (condition === 5 && isSucceed) {
      btnProps.animTimeline = showSuccessIcon
      btnProps.animTimelineCb = successCb
      btnProps.icon = 'btn_check'
      btnProps.iconColor = 'white'
      content = null
    } else if (condition === 5 && !isSucceed) {
      btnProps.animTimeline = showFailedIcon
      btnProps.animTimelineCb = failureCb
      btnProps.icon = 'btn_close'
      btnProps.iconColor = 'white'
      content = null
    }

    return <ButtonRoundedBase {...btnProps}>{content}</ButtonRoundedBase>
  }
}
